/**
 * React hook for real-time feedback updates
 */
import { useState, useEffect, useCallback } from 'react';
import websocketService, { WebSocketMessage, FeedbackUpdateMessage } from '../services/websocketService';
import { useApiService } from '../services/apiService';

export interface FeedbackUpdateData {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  app_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

export interface RealTimeFeedbackState {
  newFeedback: FeedbackUpdateData[];
  statusUpdates: { feedback_id: string; status: string; updated_by?: string }[];
  comments: { feedback_id: string; comment: any }[];
  isConnected: boolean;
  lastUpdate: Date | null;
}

export const useRealTimeFeedback = () => {
  const apiService = useApiService();
  const [state, setState] = useState<RealTimeFeedbackState>({
    newFeedback: [],
    statusUpdates: [],
    comments: [],
    isConnected: false,
    lastUpdate: null
  });
  const [loading, setLoading] = useState(true);

  const handleFeedbackUpdate = useCallback((message: FeedbackUpdateMessage) => {
    const now = new Date();
    
    setState(prev => {
      const newState = { ...prev, lastUpdate: now };
      
      switch (message.event) {
        case 'new_feedback':
          // Add new feedback to the beginning of the array, avoiding duplicates
          const existingIndex = prev.newFeedback.findIndex(f => f.id === message.data.id);
          if (existingIndex === -1) {
            newState.newFeedback = [message.data, ...prev.newFeedback.slice(0, 99)]; // Keep last 100
          } else {
            // Update existing feedback
            newState.newFeedback = [
              message.data,
              ...prev.newFeedback.slice(0, existingIndex),
              ...prev.newFeedback.slice(existingIndex + 1, 99)
            ];
          }
          break;
          
        case 'status_change':
          // Add status update
          newState.statusUpdates = [message.data, ...prev.statusUpdates.slice(0, 49)]; // Keep last 50
          break;
          
        case 'new_comment':
          // Add new comment
          newState.comments = [message.data, ...prev.comments.slice(0, 49)]; // Keep last 50
          break;
      }
      
      return newState;
    });
  }, []);

  const handleMessage = useCallback((message: WebSocketMessage) => {
    // Update connection state
    setState(prev => ({
      ...prev,
      isConnected: websocketService.isConnected()
    }));

    // Handle feedback updates
    if (message.type === 'feedback_update') {
      handleFeedbackUpdate(message as FeedbackUpdateMessage);
    }
  }, [handleFeedbackUpdate]);

  const clearNewFeedback = useCallback(() => {
    setState(prev => ({
      ...prev,
      newFeedback: []
    }));
  }, []);

  const clearStatusUpdates = useCallback(() => {
    setState(prev => ({
      ...prev,
      statusUpdates: []
    }));
  }, []);

  const clearComments = useCallback(() => {
    setState(prev => ({
      ...prev,
      comments: []
    }));
  }, []);

  const clearAll = useCallback(() => {
    setState(prev => ({
      ...prev,
      newFeedback: [],
      statusUpdates: [],
      comments: []
    }));
  }, []);

  const subscribeToFeedback = useCallback(() => {
    websocketService.subscribeToFeedback();
  }, []);

  const loadExistingFeedback = useCallback(async () => {
    console.log('🚀 useRealTimeFeedback: Loading existing feedback', {
      timestamp: new Date().toISOString(),
      hasApiService: !!apiService
    });
    
    try {
      setLoading(true);
      
      const params = {
        page: 1,
        per_page: 20,
        include_user: true,
        include_app: true
      };
      
      console.log('📤 useRealTimeFeedback: API request params', params);
      
      const response = await apiService.getFeedbackList(params);
      
      console.log('📥 useRealTimeFeedback: API response received', {
        hasData: !!response.data,
        itemsLength: response.data?.items?.length || 0,
        total: response.data?.total,
        responseStructure: Object.keys(response.data || {})
      });
      
      if (response.data.items.length > 0) {
        console.log('✅ useRealTimeFeedback: Setting feedback items', {
          itemCount: response.data.items.length,
          firstItem: response.data.items[0]
        });
        
        setState(prev => ({
          ...prev,
          newFeedback: response.data.items,
          lastUpdate: new Date()
        }));
      } else {
        console.log('⚠️ useRealTimeFeedback: No feedback items received', {
          responseData: response.data
        });
      }
    } catch (error) {
      console.error('❌ useRealTimeFeedback: Failed to load existing feedback', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined
      });
    } finally {
      setLoading(false);
      console.log('🏁 useRealTimeFeedback: Loading completed', {
        loading: false,
        timestamp: new Date().toISOString()
      });
    }
  }, [apiService]);

  const getRecentFeedback = useCallback((limit: number = 10) => {
    return state.newFeedback.slice(0, limit);
  }, [state.newFeedback]);

  const getRecentStatusUpdates = useCallback((limit: number = 10) => {
    return state.statusUpdates.slice(0, limit);
  }, [state.statusUpdates]);

  const getRecentComments = useCallback((limit: number = 10) => {
    return state.comments.slice(0, limit);
  }, [state.comments]);

  const getFeedbackCount = useCallback(() => {
    return state.newFeedback.length;
  }, [state.newFeedback.length]);

  const getStatusUpdateCount = useCallback(() => {
    return state.statusUpdates.length;
  }, [state.statusUpdates.length]);

  const getCommentCount = useCallback(() => {
    return state.comments.length;
  }, [state.comments.length]);

  useEffect(() => {
    console.log('🔄 useRealTimeFeedback: useEffect triggered - component mounted or dependencies changed', {
      timestamp: new Date().toISOString(),
      isWebSocketConnected: websocketService.isConnected(),
      hasApiService: !!apiService
    });
    
    // Load existing feedback on mount
    loadExistingFeedback();
    
    websocketService.addEventHandler(handleMessage);

    // Subscribe to feedback updates when connected
    if (websocketService.isConnected()) {
      console.log('📡 useRealTimeFeedback: WebSocket already connected, subscribing to feedback');
      subscribeToFeedback();
    } else {
      console.log('📡 useRealTimeFeedback: WebSocket not connected yet');
    }

    return () => {
      console.log('🧹 useRealTimeFeedback: Cleanup - removing WebSocket event handler');
      websocketService.removeEventHandler(handleMessage);
    };
  }, [handleMessage, subscribeToFeedback, loadExistingFeedback]);

  return {
    ...state,
    loading,
    clearNewFeedback,
    clearStatusUpdates,
    clearComments,
    clearAll,
    subscribeToFeedback,
    loadExistingFeedback,
    getRecentFeedback,
    getRecentStatusUpdates,
    getRecentComments,
    getFeedbackCount,
    getStatusUpdateCount,
    getCommentCount
  };
};

export default useRealTimeFeedback;